import React, {useCallback, useRef, useState} from 'react';

import BenchlingConfig from 'BenchlingConfig';
import {getSearchResultPropsForIndex} from 'notebook/mediocre/search/SearchResultProps';
import {isColumnOutdated} from 'notebook/structured-tables/columns/ColumnDefinitions';
import getColumnConstraintWithRowData from 'notebook/structured-tables/columns/getColumnConstraint';
import {getLastSyncedUpstreamValueWithColumnConstraint} from 'notebook/structured-tables/util/getLastSyncedUpstreamValue';
import TableCell from 'notebook/tables/core-ui/TableCell';
import {allowDeleteColumn, getCellProps} from 'notebook/tables/core-ui/TableCellRenderUtils';
import {getIconPrefix, getCellComponent, TableHeaderRowProps} from 'notebook/tables/core-ui/TableHeaderRow';
import {TableRowProps} from 'notebook/tables/core-ui/TableRow';
import UnitsTableAxisCell from 'notebook/tables/core-ui/UnitsTableAxisCell';
import {useRowModel} from 'notebook/tables/store/hooks/useRowModel';
import {isTextEditableType, TABLE_COLUMN} from 'notebook/tables/TableConstants';
import TableUtil from 'notebook/tables/TableUtil';
import {formatTypedValue} from 'notebook/tables/typed-values/DataTypes';
import getTestProps from 'test/lib/cypress/getTestProps';

const DEFAULT_SINGLE_INPUT_WIDTH = 150;

export interface TableSingleInputProps {
  j: number;
  headerRowProps: TableHeaderRowProps;
  rowProps: TableRowProps;
  columnWidth?: number;
  setColumnWidth?: (columnIndex: number, width: number) => void;
  locked?: boolean;
}

export default function TableSingleInput(props: TableSingleInputProps): JSX.Element {
  const i = 0;
  const {j, headerRowProps, rowProps, columnWidth, setColumnWidth, locked} = props;
  const {minJ, maxJ, table, viewer} = headerRowProps;
  const rowModel = useRowModel();
  const containerRef = useRef<HTMLDivElement>(null);
  const [isResizing, setIsResizing] = useState(false);

  const handleResizeMouseDown = useCallback(
    (e: React.MouseEvent) => {
      if (locked || viewer || !setColumnWidth) {
        return;
      }
      e.preventDefault();
      e.stopPropagation();
      setIsResizing(true);

      const startX = e.clientX;
      const startWidth = containerRef.current?.offsetWidth || columnWidth || DEFAULT_SINGLE_INPUT_WIDTH;

      const handleMouseMove = (moveEvent: MouseEvent): void => {
        const delta = moveEvent.clientX - startX;
        const newWidth = Math.max(DEFAULT_SINGLE_INPUT_WIDTH, startWidth + delta);
        if (containerRef.current) {
          containerRef.current.style.width = `${newWidth}px`;
        }
      };

      const handleMouseUp = (upEvent: MouseEvent): void => {
        setIsResizing(false);
        document.removeEventListener('mousemove', handleMouseMove);
        document.removeEventListener('mouseup', handleMouseUp);

        const delta = upEvent.clientX - startX;
        const newWidth = Math.max(DEFAULT_SINGLE_INPUT_WIDTH, startWidth + delta);
        setColumnWidth(j, newWidth);
      };

      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
    },
    [locked, viewer, setColumnWidth, columnWidth, j]
  );

  // TODO(BNCH-113857): Remove this once selectors are enabled
  // For now, rowState may be null if selectors are disabled.  Pass along the props if so
  const {row, evaluatedRow, rowProperties, rowData, columnConstraints} = rowModel || props.rowProps;

  const renderHeader = (): JSX.Element => {
    const w = table.table[0].length;
    const editable = !viewer && !headerRowProps.locked && TableUtil.allowColumnEdits(table) && j < w;
    const columnConstraint = table.columnConstraints[j];
    const CellComponent = getCellComponent(table, j);

    return (
      <CellComponent
        key={`axis-${j}`}
        {...headerRowProps}
        renderedLabel={TableUtil.getRenderedColumnLabel(table, j)}
        shouldHighlightCell={false}
        label={TableUtil.getColumnLabel(table, j)}
        index={j}
        dim={TABLE_COLUMN}
        {...(editable && {
          delDim: headerRowProps.deleteColumns,
          renameDim: headerRowProps.renameColumn,
          // Single Inputs do not allow inserting table columns left or right
          insertDimBefore: undefined,
          insertDimAfter: undefined,
        })}
        {...(editable &&
          j === 0 &&
          !headerRowProps.disableLegacyHeader && {toggleHeader: headerRowProps.toggleHeaderColHandler})}
        numDims={w}
        cellDragStart={headerRowProps.axisCellDragStartHandler}
        cellDragOver={headerRowProps.axisCellDragOverHandler}
        isGhostCell={false}
        isSelected={minJ != null && maxJ != null && minJ <= j && j <= maxJ}
        {...(CellComponent === UnitsTableAxisCell && {locked: headerRowProps.locked})}
        {...(columnConstraint != null && {columnConstraint})}
        disallowCustomColumns={TableUtil.disallowCustomColumns(table)}
        shouldDisableInsertDimBefore={true}
        shouldDisableInsertDimAfter={true}
        shouldDisableDeleteDim={!allowDeleteColumn({...headerRowProps, columnIndex: j})}
        disableInsertDimTooltip={table._disableInsertColsTooltip}
        iconPrefix={
          headerRowProps.hideColumnHeaderIcons
            ? null
            : getIconPrefix(headerRowProps.fileId, table, columnConstraint)
        }
        isLeftAlign={headerRowProps.isLeftAlignColumnHeader}
        columnIsOutdated={isColumnOutdated(TableUtil.getColumnDefinition(table, j))}
        isHeaderCell={true}
        dataTestAttrs={{component: 'TableSingleInput', element: 'columnLabelCell'}}
        isSingleInputHeader={true}
        {...getSearchResultPropsForIndex(headerRowProps, j)}
      />
    );
  };

  const renderCell = (): JSX.Element | null => {
    const {isCellLocked, isCellSelected, isCellDblSelected, hasActiveEdit, cellTypedValue, isTombstoned} =
      getCellProps(rowProps, row, evaluatedRow, rowProperties, j);

    if (TableUtil.isNotRendered(i, j, evaluatedRow[j])) {
      return null;
    }

    const columnConstraint = getColumnConstraintWithRowData(columnConstraints[j], rowData);
    const lastSyncedUpstreamValue =
      getLastSyncedUpstreamValueWithColumnConstraint(rowData?.lastSyncedUpstreamValues, columnConstraint) ??
      undefined;

    return (
      <TableCell
        {...rowProps}
        {...{
          isSingleInputCell: true,
          locked: isCellLocked,
          cid: rowProps.cellCid(i, j),

          // TODO(BNCH-113857): Remove this once selectors are enabled
          // If we're not using selectors, then we need to pass the cell data directly
          ...(BenchlingConfig.ELN_TABLE_ENABLE_STATE_SELECTORS
            ? {}
            : {
                cell: {
                  ...row[j],
                  text:
                    row[j].text ||
                    (cellTypedValue != null && isTextEditableType(cellTypedValue)
                      ? formatTypedValue(cellTypedValue)
                      : ''),
                  // grayOutContents is only set for StructuredTables
                  ...(isTombstoned && {grayOutContents: true}),
                },
                evaluatedCell: evaluatedRow[j],
                columnConstraint,
                rowData,
              }),

          i,
          j,
          isHeaderCell: false,
          hasActiveEdit,
          lastSyncedUpstreamValue,
          highlightClass: rowProps.cellColors != null ? rowProps.cellColors[j] : undefined,
          isFirstColumn: false,
          isSelected: isCellSelected,
          isDoubleSelected: isCellDblSelected,
          fillDownSquareMouseDownHandler: null,
          fillDownSquareDoubleClickHandler: null,
          hasRowBeenInView: true,
          isFillDownBoxLeftEdge: false,
          isFillDownBoxRightEdge: false,
          isFillDownBoxTopEdge: false,
          isFillDownBoxBottomEdge: false,
        }}
        {...getSearchResultPropsForIndex(rowProps, j)}
      />
    );
  };

  const canResize = !locked && !viewer && setColumnWidth != null;
  // if no width or less than the minimum width, defer to CSS to handle initial sizing (100% of container)
  const effectiveWidth = !columnWidth || columnWidth < DEFAULT_SINGLE_INPUT_WIDTH ? undefined : columnWidth;

  return (
    <div
      ref={containerRef}
      key={`singleInput-${j}`}
      className={`mediocre-tableEditable-singleInput ${isResizing ? 'is-resizing' : ''}`}
      style={{width: effectiveWidth}}
      onClick={(e) => {
        // prevent the click from propagating to the single input section's clearMultiSelect
        e.stopPropagation();
      }}
      {...getTestProps({component: 'SingleInput', element: 'root'})}
    >
      <table style={{borderCollapse: 'separate', width: '100%'}}>
        <tbody>
          <tr>{renderHeader()}</tr>
        </tbody>
      </table>
      <div className="mediocre-tableEditable-singleInput-cellWrapper">
        <table style={{borderCollapse: 'separate', width: '100%'}}>
          <tbody>
            <tr>{renderCell()}</tr>
          </tbody>
        </table>
        {canResize && (
          <div
            className="mediocre-tableEditable-singleInput-resizeHandle"
            onMouseDown={handleResizeMouseDown}
            {...getTestProps({component: 'SingleInput', element: 'resizeHandle'})}
          />
        )}
      </div>
    </div>
  );
}
